import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { plansAPI, jobsAPI } from '@/utils/api';
import { PopularSectors } from '@/components/PopularSectors';
import { TrustedCompanies } from '@/components/TrustedCompanies';
import { HowToApply } from '@/components/HowToApply';
import { PromoCountdown } from '@/components/PromoCountdown';
import { usePWA } from '@/contexts/PWAContext';
import { RemoteBadge } from '@/components/RemoteBadge';
import { PioneerWall } from '@/components/PioneerWall';
import { rwLabel } from '@/constants/remoteWork';
import {
  Search, Briefcase, ArrowRight, MapPin, Star, Check,
  ShieldCheck, ChevronDown, ChevronUp, Mail,
  Smartphone, Download, Award, Building2, Clock, Globe2,
} from 'lucide-react';

/**
 * Trabadju — Public homepage (redesigned 2026-06)
 * Focus: credibility, clarity, conversion. Mobile-first. One H1.
 * Semantic <main> / <section> / <article>. Sober palette (navy + amber).
 */

const FAQ_KEYS = [0, 1, 2, 3, 4];

export default function Landing() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'pt').slice(0, 2);
  const pickLocalized = (obj, field) => {
    if (!obj) return '';
    const key = `${field}_${lang}`;
    if (obj[key] && String(obj[key]).trim()) return obj[key];
    if (field === 'category') return obj.category || '';
    return obj[`${field}_pt`] || '';
  };
  const pickCategory = (job) => pickLocalized(job, 'category');
  const [recentJobs, setRecentJobs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const [featuredJob, setFeaturedJob] = useState(null);
  const { isInstallable, isInstalled, install, isIOS } = usePWA();

  useEffect(() => {
    jobsAPI
      .getPublic({ limit: 6 })
      .then((r) => setRecentJobs(r.data || []))
      .catch(() => setRecentJobs([]));
    plansAPI.getPlans().then((r) => setPlans(r.data || [])).catch(() => {});
    // Carregar vaga em destaque
    jobsAPI
      .getPublic({ featured: true, limit: 1 })
      .then((r) => { const jobs = r.data || []; if (jobs.length) setFeaturedJob(jobs[0]); })
      .catch(() => {});
  }, []);

  const fmtSalary = (j) => {
    if (j.salary_range_min && j.salary_range_max) {
      const a = Number(j.salary_range_min).toLocaleString('pt-PT');
      const b = Number(j.salary_range_max).toLocaleString('pt-PT');
      return `${a}–${b} ${j.currency || 'CVE'}`;
    }
    return t('home.salaryTBD');
  };

  return (
    <main className="bg-white text-slate-900">
      <Helmet>
        <title>{t('home.seoTitle')}</title>
        <meta name="description" content={t('home.seoDescription')} />
        <link rel="canonical" href="https://trabadju.cv/" />
      </Helmet>
      {/* ════════════════════ HERO ════════════════════ */}
      <section
        aria-labelledby="hero-title"
        className="relative bg-navy border-b border-[#1a3363]"
      >
        {/* subtle dot texture */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(61,99,245,0.18), transparent 50%), radial-gradient(circle at 10% 90%, rgba(61,99,245,0.10), transparent 50%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28 lg:pt-28">
          {/* eyebrow */}
          <p className="text-xs sm:text-[13px] font-semibold tracking-[0.10em] uppercase text-sky mb-4 sm:mb-5">
            {t('home.eyebrow')}
          </p>

          <h1
            id="hero-title"
            className="max-w-3xl text-[34px] leading-[1.08] sm:text-[44px] lg:text-[54px] lg:leading-[1.06] font-bold tracking-tighter text-white"
            data-testid="hero-title"
          >
            {t('home.heroTitlePart1')}{' '}
            <span className="text-cta">{t('home.heroTitlePart2')}</span>
          </h1>

          <p className="mt-5 sm:mt-6 max-w-xl text-[15px] sm:text-base lg:text-[17px] text-blue-100 leading-[1.6]">
            {t('home.lead')}
          </p>

          {/* CTAs — distinct hierarchy, one primary */}
          <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => navigate('/jobs/search')}
              className="group inline-flex items-center justify-center gap-2 h-12 px-6 bg-brand hover:bg-blue-600 active:bg-blue-800 text-white text-[15px] font-semibold rounded-lg shadow-sm transition focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-200"
              data-testid="hero-search-jobs-btn"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
              {t('home.searchCta')}
              <ArrowRight className="h-4 w-4 opacity-70 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-cta text-navy text-[15px] font-semibold rounded-lg shadow-sm hover:bg-amber-400 transition focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-200"
              data-testid="hero-post-job-btn"
            >
              <Briefcase className="h-4 w-4" aria-hidden="true" />
              {t('home.publishCta')}
            </button>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px]">
            <button
              type="button"
              onClick={() => navigate('/jobs/search?mode=remoto')}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-emerald-300/60 bg-emerald-400/10 text-emerald-100 font-semibold hover:bg-emerald-400/20 transition-colors"
              data-testid="hero-remote-jobs-btn"
            >
              <Globe2 className="h-4 w-4" aria-hidden="true" />
              {rwLabel(i18n.language, 'remoteShortcut')}
            </button>
            <Link to="/empresas-internacionais" className="text-blue-200 hover:text-white underline-offset-4 hover:underline" data-testid="hero-intl-companies-link">
              {rwLabel(i18n.language, 'intlLink')} →
            </Link>
          </div>

          {/* tiny trust line */}
          <p className="mt-7 text-[13px] text-blue-200 max-w-2xl leading-[1.55]">
            {t('home.trustLinePrefix')} <strong className="text-blue-100 font-semibold">Tavares Groupe — Sociedade Unipessoal Lda</strong> (NIF&nbsp;213407175) · {t('home.trustLineSuffix')}
          </p>
        </div>
      </section>

      {/* ════════════════════ MURAL DE CONFIANÇA ════════════════════ */}
      <TrustedCompanies />

      {/* ════════════════════ VAGA EM DESTAQUE ════════════════════ */}
      {featuredJob && (
        <section
          aria-labelledby="featured-job-title"
          className="border-b border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50"
          data-testid="featured-job-section"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 sm:py-8">
            {/* Badge milestone */}
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-700 text-white text-[11px] font-bold tracking-wider uppercase">
                <Award className="h-3 w-3" />
                1.ª Vaga Empresarial Oficial em Cabo Verde
              </span>
              <span className="text-xs text-slate-500 font-medium">publicada hoje</span>
            </div>

            <div
              className="group flex flex-col sm:flex-row sm:items-center gap-5 bg-white rounded-2xl border-2 border-amber-300 hover:border-amber-500 shadow-sm hover:shadow-md transition-all p-5 sm:p-6 cursor-pointer"
              onClick={() => navigate(`/jobs/${featuredJob.id}`)}
              data-testid="featured-job-card"
            >
              {/* Ícone empresa */}
              <div className="flex-none w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-amber-100 flex items-center justify-center border border-amber-200">
                <Building2 className="h-7 w-7 text-amber-700" />
              </div>

              {/* Conteúdo */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h2 id="featured-job-title" className="text-lg sm:text-xl font-bold text-slate-900 group-hover:text-amber-700 transition-colors">
                    {featuredJob.title_pt || featuredJob.title}
                  </h2>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-700 text-white">
                    <Star className="h-2.5 w-2.5" /> Destaque
                  </span>
                  {featuredJob.company_verified && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700">
                      <ShieldCheck className="h-2.5 w-2.5" /> Verificada
                    </span>
                  )}
                </div>
                <p className="font-semibold text-slate-700 text-sm">{featuredJob.company_name}</p>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-amber-600" />
                    {featuredJob.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-3.5 w-3.5" />
                    {featuredJob.employment_type === 'full-time' ? 'Tempo Inteiro' : featuredJob.employment_type}
                  </span>
                  {featuredJob.salary_range_min && (
                    <span className="flex items-center gap-1 font-medium text-slate-700">
                      {Number(featuredJob.salary_range_min).toLocaleString('pt-PT')}
                      {featuredJob.salary_range_max ? `–${Number(featuredJob.salary_range_max).toLocaleString('pt-PT')}` : '+'} CVE
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Hoje
                  </span>
                </div>
              </div>

              {/* CTA */}
              <div className="flex-none">
                <span className="inline-flex items-center gap-2 h-11 px-5 bg-amber-700 hover:bg-amber-800 text-white text-sm font-semibold rounded-xl transition group-hover:shadow-md">
                  Ver Vaga
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ════════════════════ APP DISPONÍVEL ════════════════════ */}
      <section
        aria-labelledby="app-download-title"
        className="border-b border-slate-800 bg-[#0b1e44]"
        data-testid="app-download-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
            {/* Ícone + texto */}
            <div className="flex items-start gap-4 flex-1">
              <div className="flex-none w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-brand flex items-center justify-center shadow-lg">
                <Smartphone className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
              </div>
              <div>
                <p className="text-xs font-bold tracking-[0.12em] uppercase text-blue-400 mb-1">Nova Funcionalidade</p>
                <h2 id="app-download-title" className="text-xl sm:text-2xl font-bold text-white leading-tight">
                  A app Trabadju já está disponível!
                </h2>
                <p className="mt-1 text-sm sm:text-base text-slate-300 leading-relaxed">
                  Instala gratuitamente no teu Android ou iPhone — sem App Store, sem complicações.
                  Abre <strong className="text-white">trabadju.cv</strong> no browser e toca em <em>Instalar</em>.
                </p>
              </div>
            </div>

            {/* Botões */}
            <div className="flex flex-col sm:flex-row gap-3 lg:flex-none">
              {!isInstalled ? (
                <>
                  <button
                    onClick={install}
                    data-testid="landing-app-install-btn"
                    className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-amber-400 hover:bg-amber-300 text-slate-900 text-sm font-bold rounded-xl transition shadow-md hover:shadow-lg"
                  >
                    <Download className="h-4 w-4" />
                    {isIOS ? 'Instalar no iPhone' : 'Instalar App Grátis'}
                  </button>
                  <a
                    href="https://trabadju.cv"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-xl border border-white/20 transition"
                    data-testid="landing-app-learn-more"
                  >
                    Saber mais
                  </a>
                </>
              ) : (
                <div className="inline-flex items-center gap-2 h-12 px-6 bg-emerald-500/20 text-emerald-300 text-sm font-semibold rounded-xl border border-emerald-500/30">
                  <Check className="h-4 w-4" />
                  App já instalada!
                </div>
              )}
            </div>
          </div>

          {/* Plataformas */}
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"></span>
              Android (Samsung, Xiaomi, etc.)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-400 inline-block"></span>
              iPhone (iOS 16.4+)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-slate-400 inline-block"></span>
              Sem necessidade de App Store
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>
              100% Grátis
            </span>
          </div>
        </div>
      </section>

      {/* ════════════════════ TRUST STRIP ════════════════════ */}
      <section
        aria-label={t('home.trustStripAria')}
        className="border-b border-slate-100 bg-mist"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
          <ul className="grid grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-8">
            {[
              { k: t('home.trustBar.islands'),    v: t('home.trustBar.islandsLabel') },
              { k: t('home.trustBar.payments'),   v: t('home.trustBar.paymentsLabel') },
              { k: t('home.trustBar.noMonthly'),  v: t('home.trustBar.noMonthlyLabel') },
              { k: t('home.trustBar.multilang'),  v: t('home.trustBar.multilangLabel') },
            ].map((it) => (
              <li key={it.v} className="border-l-2 border-brand pl-3 sm:pl-4">
                <p className="text-xs sm:text-sm uppercase tracking-widest font-semibold text-slate-500">{it.v}</p>
                <p className="mt-1 text-base sm:text-lg font-semibold text-navy">{it.k}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ════════════════════ COMO FUNCIONA — DOIS PERCURSOS ════════════════════ */}
      <section
        aria-labelledby="howitworks-title"
        className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24"
      >
        <header className="max-w-2xl mb-12 sm:mb-16">
          <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-3">
            {t('home.howEyebrow')}
          </p>
          <h2
            id="howitworks-title"
            className="text-[26px] sm:text-3xl lg:text-[36px] font-bold text-slate-900 tracking-tighter leading-[1.15]"
           
          >
            {t('home.howTitle')}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            {t('home.howSubtitle')}
          </p>
        </header>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10">
          {/* ─── Candidato ─── */}
          <article
            className="relative p-8 sm:p-10 rounded-2xl bg-white border border-slate-200 hover:border-slate-900 transition-colors"
            aria-labelledby="path-candidate"
            data-testid="path-candidate"
          >
            <div className="flex items-center gap-3 mb-1">
              <span className="text-xs font-semibold tracking-[0.10em] uppercase text-amber-700">
                {t('home.candidateLabel')}
              </span>
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-emerald-50 text-emerald-700 rounded">
                {t('home.candidateFreeBadge')}
              </span>
            </div>
            <h3 id="path-candidate" className="text-2xl sm:text-3xl font-bold text-slate-900 mt-2 mb-6">
              {t('home.candidateTitle')}
            </h3>
            <ol className="space-y-5 mb-8">
              {[
                [t('home.candidateStep1Title'), t('home.candidateStep1Body')],
                [t('home.candidateStep2Title'), t('home.candidateStep2Body')],
                [t('home.candidateStep3Title'), t('home.candidateStep3Body')],
              ].map(([title, body], i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex-none w-8 h-8 rounded-full bg-amber-100 text-amber-900 font-bold flex items-center justify-center text-sm">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{title}</p>
                    <p className="text-sm sm:text-base text-slate-600 mt-0.5 leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link
              to="/candidate/register"
              className="inline-flex items-center gap-2 h-12 px-6 bg-amber-700 hover:bg-amber-800 text-white text-sm sm:text-base font-semibold rounded-lg transition focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-200"
              data-testid="path-candidate-cta"
            >
              {t('home.candidateCta')}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <p className="mt-3 text-xs text-slate-500">
              {t('home.alreadyHaveAccount')} <Link to="/candidate/login" className="underline hover:text-amber-700">{t('home.loginAction')}</Link>
            </p>
          </article>

          {/* ─── Empresa ─── */}
          <article
            className="relative p-8 sm:p-10 rounded-2xl bg-slate-900 text-white border border-slate-900"
            aria-labelledby="path-employer"
            data-testid="path-employer"
          >
            <div className="flex items-center gap-3 mb-1">
              <span className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-300">
                {t('home.employerLabel')}
              </span>
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-blue-500/20 text-blue-200 rounded">
                {t('home.employerPriceBadge')}
              </span>
            </div>
            <h3 id="path-employer" className="text-2xl sm:text-3xl font-bold text-white mt-2 mb-6">
              {t('home.employerTitle')}
            </h3>
            <ol className="space-y-5 mb-8">
              {[
                [t('home.employerStep1Title'), t('home.employerStep1Body')],
                [t('home.employerStep2Title'), t('home.employerStep2Body')],
                [t('home.employerStep3Title'), t('home.employerStep3Body')],
              ].map(([title, body], i) => (
                <li key={i} className="flex gap-4">
                  <span className="flex-none w-8 h-8 rounded-full bg-blue-500/20 text-blue-100 font-bold flex items-center justify-center text-sm">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-white">{title}</p>
                    <p className="text-sm sm:text-base text-slate-300 mt-0.5 leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 h-12 px-6 bg-white text-slate-900 hover:bg-slate-100 text-sm sm:text-base font-semibold rounded-lg transition focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300"
              data-testid="path-employer-cta"
            >
              {t('home.employerCta')}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <p className="mt-3 text-xs text-slate-400">
              {t('home.alreadyHaveAccount')} <Link to="/login" className="underline hover:text-white">{t('home.loginAction')}</Link>
            </p>
          </article>
        </div>
      </section>

      <PopularSectors />

      {/* ════════════════════ VAGAS RECENTES ════════════════════ */}
      {recentJobs.length > 0 && (
        <section
          aria-labelledby="recent-jobs-title"
          className="border-t border-slate-100 bg-slate-50/50"
          data-testid="recent-jobs-section"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10 sm:mb-12">
              <div>
                <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-3">
                  {t('home.recentEyebrow')}
                </p>
                <h2
                  id="recent-jobs-title"
                  className="text-[26px] sm:text-3xl lg:text-[36px] font-bold text-slate-900 tracking-tighter leading-[1.15]"
                 
                >
                  {t('home.recentTitle')}
                </h2>
              </div>
              <Link
                to="/jobs/search"
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900"
                data-testid="recent-jobs-see-all"
              >
                {t('home.recentSeeAll')}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </header>

            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {recentJobs.map((job) => (
                <li key={job.id} className="contents">
                  <article
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    className="group bg-white rounded-xl border border-slate-200 p-6 cursor-pointer hover:border-slate-900 hover:shadow-sm transition flex flex-col"
                    data-testid={`recent-job-card-${job.id}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="text-lg font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-blue-700 transition-colors">
                        {pickLocalized(job, 'title') || t('home.untitledJob')}
                      </h3>
                      <div className="flex-none flex items-center gap-1">
                        {job.company_verified && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-blue-50 text-blue-800 rounded"
                            aria-label={t('home.verifiedBadge')}
                            data-testid={`job-verified-${job.id}`}
                          >
                            <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                            {t('home.verifiedBadge')}
                          </span>
                        )}
                        <RemoteBadge job={job} />
                        {job.is_featured && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-amber-100 text-amber-800 rounded"
                            aria-label={t('home.featuredBadge')}
                          >
                            <Star className="h-3 w-3 fill-amber-500 text-amber-500" aria-hidden="true" />
                            {t('home.featuredBadge')}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 mb-4">
                      {job.category && (
                        <span className="inline-flex items-center gap-1">
                          <Briefcase className="h-3.5 w-3.5" aria-hidden="true" />
                          {pickCategory(job)}
                        </span>
                      )}
                      {job.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                          {job.location}
                        </span>
                      )}
                    </p>
                    {pickLocalized(job, 'description') && (
                      <p className="text-sm text-slate-600 leading-relaxed line-clamp-2 mb-5">{pickLocalized(job, 'description')}</p>
                    )}
                    <footer className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900">{fmtSalary(job)}</p>
                      <span
                        aria-hidden="true"
                        className="text-xs font-semibold text-blue-700 group-hover:translate-x-1 transition-transform"
                      >
                        {t('home.viewMore')}
                      </span>
                    </footer>
                  </article>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ════════════════════ COMO SE CANDIDATAR ════════════════════ */}
      <HowToApply />

      <PioneerWall />

      {/* ════════════════════ PLANOS ════════════════════ */}
      {plans.length > 0 && (
        <section
          aria-labelledby="plans-title"
          className="border-t border-slate-100 max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24"
          data-testid="plans-section"
        >
          <header className="max-w-2xl mb-12 sm:mb-14">
            <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-3">
              {t('home.plansEyebrow')}
            </p>
            <h2
              id="plans-title"
              className="text-[26px] sm:text-3xl lg:text-[36px] font-bold text-slate-900 tracking-tighter leading-[1.15]"
             
              data-testid="plans-section-title"
            >
              {t('home.plansTitle')}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              {t('home.plansSubtitle')}
            </p>
          </header>

          <PromoCountdown plans={plans} className="mb-8" />

          <ul className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {plans.map((p) => {
              const isFeatured = p.plan_type === 'featured';
              const planName = p[`name_${i18n.language}`] || t(`home.planNames.${p.plan_type}`, { defaultValue: p.name_pt });
              const promo = p.promo;
              const promoTag = promo
                ? (promo.kind === 'percent' ? `-${promo.value}%` : promo.kind === 'fixed' ? `-${promo.value.toLocaleString('pt-PT')} CVE` : `+${promo.value} ${t('home.promoBonusCredits')}`)
                : null;
              return (
                <li key={p.plan_type} className="contents">
                  <article
                    className={
                      'rounded-xl p-6 border flex flex-col transition ' +
                      (isFeatured
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-900 border-slate-200 hover:border-slate-900')
                    }
                    data-testid={`plan-card-${p.plan_type}`}
                  >
                    <p
                      className={
                        'text-xs uppercase tracking-widest font-semibold mb-3 ' +
                        (isFeatured ? 'text-amber-300' : 'text-blue-700')
                      }
                    >
                      {planName}
                      {promoTag && (
                        <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-rose-600 text-white text-[10px] font-bold tracking-normal normal-case align-middle" data-testid={`plan-promo-${p.plan_type}`}>
                          {promoTag}
                        </span>
                      )}
                    </p>
                    <p className="mb-5">
                      {p.price_cve === 0 ? (
                        <span className="text-3xl font-extrabold">{t('home.planFree')}</span>
                      ) : (
                        <>
                          {promo && promo.saving_cve > 0 && (
                            <span className={'block text-sm line-through ' + (isFeatured ? 'text-slate-400' : 'text-slate-400')}>
                              {p.price_cve.toLocaleString('pt-PT')} CVE
                            </span>
                          )}
                          <span className="text-3xl font-extrabold">
                            {(promo && promo.saving_cve > 0 ? promo.promo_price_cve : p.price_cve).toLocaleString(i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'en' ? 'en-US' : 'pt-PT')}
                          </span>
                          <span className={'ml-1 text-sm ' + (isFeatured ? 'text-slate-300' : 'text-slate-500')}>CVE</span>
                          {promo?.ends_at && (
                            <span className="block text-xs text-rose-600 mt-1 font-medium">
                              {t('home.promoUntil', { date: new Date(promo.ends_at).toLocaleDateString('pt-PT') })}
                            </span>
                          )}
                        </>
                      )}
                    </p>
                    <ul className="space-y-2 mb-6 flex-grow">
                      {(() => {
                        const localized = t(`home.planFeatures.${p.plan_type}`, { returnObjects: true, defaultValue: null });
                        const features = (Array.isArray(localized) && localized.length) ? localized : p.features_pt;
                        return features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                            <Check className={'h-4 w-4 mt-0.5 flex-none ' + (isFeatured ? 'text-amber-300' : 'text-blue-700')} aria-hidden="true" />
                            <span className={isFeatured ? 'text-slate-200' : 'text-slate-700'}>{f}</span>
                          </li>
                        ));
                      })()}
                    </ul>
                    <button
                      type="button"
                      onClick={() => navigate('/register')}
                      className={
                        'h-11 px-4 rounded-lg text-sm font-semibold transition focus:outline-none focus-visible:ring-4 ' +
                        (isFeatured
                          ? 'bg-white text-slate-900 hover:bg-slate-100 focus-visible:ring-amber-300'
                          : 'bg-slate-900 text-white hover:bg-slate-800 focus-visible:ring-slate-300')
                      }
                      data-testid={`plan-cta-${p.plan_type}`}
                    >
                      {p.price_cve === 0 ? t('home.planStartFree') : t('home.planChoose')}
                    </button>
                  </article>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* ════════════════════ FAQ ════════════════════ */}
      <section
        aria-labelledby="faq-title"
        className="border-t border-slate-100 bg-slate-50/50"
      >
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <header className="mb-10 sm:mb-12">
            <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-3">
              {t('home.faqEyebrow')}
            </p>
            <h2
              id="faq-title"
              className="text-[26px] sm:text-3xl lg:text-[36px] font-bold text-slate-900 tracking-tighter leading-[1.15]"
             
              data-testid="faq-section-title"
            >
              {t('home.faqTitle')}
            </h2>
          </header>
          <ul className="space-y-2">
            {FAQ_KEYS.map((i) => {
              const isOpen = openFaq === i;
              const q = t(`home.faq.${i}.q`);
              const a = t(`home.faq.${i}.a`);
              return (
                <li key={i} className="contents">
                  <article className="bg-white rounded-lg border border-slate-200" data-testid={`faq-item-${i}`}>
                    <h3>
                      <button
                        type="button"
                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                        aria-expanded={isOpen}
                        className="w-full px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between text-left gap-4 group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 rounded-lg"
                        data-testid={`faq-toggle-${i}`}
                      >
                        <span className="font-semibold text-slate-900 text-base sm:text-lg">{q}</span>
                        {isOpen ? (
                          <ChevronUp className="h-5 w-5 text-slate-500 flex-none" aria-hidden="true" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-500 flex-none" aria-hidden="true" />
                        )}
                      </button>
                    </h3>
                    {isOpen && (
                      <div className="px-5 sm:px-6 pb-5 sm:pb-6 -mt-1 text-sm sm:text-base text-slate-600 leading-relaxed">
                        {a}
                      </div>
                    )}
                  </article>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ════════════════════ BLOCO INSTITUCIONAL ════════════════════ */}
      <section
        aria-labelledby="about-title"
        className="border-t border-slate-100 bg-slate-900 text-white"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <div className="grid lg:grid-cols-3 gap-10 lg:gap-16">
            <div className="lg:col-span-2">
              <p className="text-xs font-semibold tracking-[0.10em] uppercase text-amber-300 mb-3">
                {t('home.aboutEyebrow')}
              </p>
              <h2
                id="about-title"
                className="text-[26px] sm:text-3xl lg:text-[36px] font-bold tracking-tighter leading-[1.15] text-white"
               
              >
                {t('home.aboutTitle')}
              </h2>
              <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
                {t('home.aboutBodyPrefix')} <strong className="text-white">Tavares Groupe — Sociedade Unipessoal Lda</strong>{t('home.aboutBodySuffix')}
              </p>
              <dl className="mt-8 grid sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <dt className="text-slate-400 uppercase tracking-widest text-[11px] font-semibold">{t('home.aboutNif')}</dt>
                  <dd className="mt-1 font-semibold text-white">213407175</dd>
                </div>
                <div>
                  <dt className="text-slate-400 uppercase tracking-widest text-[11px] font-semibold">{t('home.aboutEmail')}</dt>
                  <dd className="mt-1 font-semibold text-white">
                    <a href="mailto:info@trabadju.cv" className="hover:text-amber-300 transition-colors">info@trabadju.cv</a>
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-400 uppercase tracking-widest text-[11px] font-semibold">{t('home.aboutCoverage')}</dt>
                  <dd className="mt-1 font-semibold text-white">{t('home.aboutCoverageValue')}</dd>
                </div>
              </dl>
            </div>

            <aside className="lg:border-l lg:border-white/10 lg:pl-12">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-amber-300 flex-none mt-0.5" aria-hidden="true" />
                  <p className="text-sm text-slate-300">{t('home.aboutTrust1')}</p>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-amber-300 flex-none mt-0.5" aria-hidden="true" />
                  <p className="text-sm text-slate-300">{t('home.aboutTrust2')}</p>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-amber-300 flex-none mt-0.5" aria-hidden="true" />
                  <p className="text-sm text-slate-300">{t('home.aboutTrust3')}</p>
                </li>
              </ul>
              <div className="mt-8 flex flex-col gap-3">
                <Link
                  to="/sobre"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 bg-white text-slate-900 hover:bg-slate-100 text-sm font-semibold rounded-lg transition"
                  data-testid="about-cta"
                >
                  {t('home.aboutCta1')}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <Link
                  to="/contacto"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 bg-transparent border border-white/30 hover:border-white text-white text-sm font-semibold rounded-lg transition"
                  data-testid="contact-cta"
                >
                  {t('home.aboutCta2')}
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
